// Manage FullStory library already loaded in the page through google tag manager
//
// Note: FullStory initializes for all users just by importing the module.
// The only method in use here is identifying a user once we know who they are. It
// will still gather data for anon users.

import { initParams } from "../../types/fullstory";

type FSFn = (command: string, params?: any) => void;

// Prefer reading from `window` so we don't ever throw `ReferenceError: FS is not defined`
declare global {
  interface Window {
    FS?: FSFn;
  }
}
export {}; // ensures this file is treated as a module so `declare global` works

const getFS = (): FSFn | undefined => window.FS;

const whenDocumentComplete = (fn: () => void) => {
  if (document.readyState === "complete") {
    fn();
    return;
  }

  const listener = () => {
    if (document.readyState === "complete") {
      fn();
      document.removeEventListener("readystatechange", listener);
    }
  };

  document.addEventListener("readystatechange", listener);
};

const FullStory = {
  identifyUser: ({ uid, data }: initParams) => {
    // Keep your existing behaviour: only identify once the document is fully loaded.
    whenDocumentComplete(() => {
      FullStory.setIdentity(uid, data);
    });
  },

  setIdentity: (uid: string, properties: any) => {
    try {
      const fs = getFS();
      if (!fs) return; // FS not loaded yet / blocked
      fs("setIdentity", { uid, properties });
      console.log("[FullStory] setIdentity called:", { uid, properties });
    } catch (error) {
      console.warn("FAILED TO SET FULLSTORY IDENTITY: ", error);
    }
  },

  event: (eventName: string, data: {}) => {
    // Commented out to avoid sending events twice to FullStory (once via GA4 and once via this function)
    // By commenting this out here, we ensure mobile behaviour is not impacted
    // const fs = getFS();
    // fs?.("trackEvent", {
    //   name: eventName,
    //   properties: data,
    // });
  },

  /**
   * This function is used in trackPageView.ts to send page data to FullStory:
   * - page
   * - screen name
   * - platform
   * - env
   * - expo version
   */
  setProperties: (properties = {}) => {
    const apply = () => {
      const fs = getFS();
      if (!fs) return; // FS not loaded yet / blocked
      fs("setProperties", { type: "page", properties });
      console.log("[FullStory] page set: ", properties);
    };

    whenDocumentComplete(apply);
  },
};

export default FullStory;
